import {
    DestinationEvent,
    EventCard,
    FormattedEvent,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { useEnvContext, useLocationContext } from '../../context';

import { Header } from '../../components/Header';
import { Subtitle } from '../../components/Subtitle';
import { ThingsToDoSection } from '../../components/ThingsToDoSection';
import './TopActivities.scss';

export const THINGS_TO_DO_AFF_CODE = 'ebdssbthingstodo';

export interface TopActivitiesProps {
    top_events: DestinationEvent[];
    num_online_events: number;
    num_physical_events: number;
}

const formatEvents = (events: DestinationEvent[]) => {
    return events.map((event: DestinationEvent) =>
        transformDestinationEvent(event),
    );
};

export const getTitleTexts = (
    num_physical_events: number,
    num_online_events: number,
    shortPlaceName?: string,
) => {
    let title = gettext("%(location)s's top activities", {
        location: shortPlaceName,
    });
    let subtitle = gettext(
        "Your plans, made easy. Check out %(location)s's most popular things to do.",
        { location: shortPlaceName },
    );
    if (num_online_events > 0 && num_physical_events > 0) {
        title = gettext('Top activities in %(location)s and Online', {
            location: shortPlaceName,
        });
        subtitle = gettext(
            'Your plans, made easy. Check out the most popular things to do in %(location)s and online.',
            { location: shortPlaceName },
        );
    } else if (num_online_events > 0 && num_physical_events == 0) {
        title = gettext('Top activities online');
        subtitle = gettext(
            'Your plans, made easy. Check out the most popular things to do online.',
        );
    }
    return {
        title,
        subtitle,
    };
};

export const TopActivities: React.FunctionComponent<TopActivitiesProps> = (
    props,
) => {
    const { top_events, num_online_events, num_physical_events } = props;
    const { shortPlaceName } = useLocationContext();
    //Get context data
    const { user, env } = useEnvContext();
    const isAuthenticated = !!user?.isAuthenticated;
    const currencyFormat = env?.currencyFormat;
    const formattedEvents = formatEvents(top_events);

    const titles = getTitleTexts(
        num_physical_events,
        num_online_events,
        shortPlaceName,
    );

    return (
        <>
            {formattedEvents.length > 0 ? (
                <ThingsToDoSection>
                    <Header title={titles.title} />
                    <Subtitle text={titles.subtitle} />
                    <div className="event-card--container">
                        {formattedEvents.map((event: FormattedEvent) => {
                            return (
                                <div
                                    className="event-card"
                                    data-testid="things-to-do-event"
                                    key={event.id}
                                >
                                    <EventCard
                                        key={event.id}
                                        heapLocationString="ThingsToDo - All"
                                        isAuthenticated={isAuthenticated}
                                        currencyFormat={currencyFormat}
                                        shouldConvertToUserTimezone={true}
                                        eventData={event as FormattedEvent}
                                        isTargetBlank={true}
                                        type={'grid'}
                                        isLazyImage={true}
                                        affCode={THINGS_TO_DO_AFF_CODE}
                                        signal={{
                                            type: 'follow',
                                            value:
                                                event.organizerFollowerCount ||
                                                0,
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </ThingsToDoSection>
            ) : (
                <></>
            )}
        </>
    );
};
